import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Archive, ArchiveRestore, Trash2, Users, Briefcase, Search, ShieldAlert } from "lucide-react";
import { fmtDate } from "@/lib/format";
import { CLOSED_COMPLAINT, DaysOpenBadge } from "@/pages/Complaints";

export default function ArchivePage() {
  const navigate = useNavigate();
  const [data, setData] = useState({ tenants: [], clients: [] });
  const [q, setQ] = useState("");
  const [complaints, setComplaints] = useState([]);
  const load = () => {
    api.get("/archive").then((r) => setData(r.data)).catch(() => {});
    api.get("/complaints").then((r) => setComplaints((r.data || []).filter((c) => CLOSED_COMPLAINT.includes(c.status)))).catch(() => {});
  };
  const reopenComplaint = async (c) => { try { await api.patch(`/complaints/${c.id}`, { status: "in_behandeling" }); toast.success("Klacht heropend"); load(); } catch { toast.error("Heropenen mislukt"); } };
  useEffect(() => { load(); }, []);

  const restoreTenant = async (t) => { try { await api.patch(`/tenants/${t.id}/archive`, { value: false }); toast.success("Debiteur teruggezet"); load(); } catch { toast.error("Terugzetten mislukt"); } };
  const deleteTenant = async (t) => { try { await api.delete(`/tenants/${t.id}`); toast.success("Debiteur definitief verwijderd"); load(); } catch { toast.error("Verwijderen mislukt"); } };
  const restoreClient = async (c) => { try { await api.patch(`/clients/${c.id}/archive`, { value: false }); toast.success("Opdrachtgever teruggezet"); load(); } catch { toast.error("Terugzetten mislukt"); } };
  const deleteClient = async (c) => {
    try { await api.delete(`/clients/${c.id}`); toast.success("Opdrachtgever definitief verwijderd"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Verwijderen mislukt"); }
  };

  const tName = (t) => `${t.first_name || ""} ${t.last_name || ""}`.trim() || "—";
  const cleanupBadge = (d) => {
    if (d === null || d === undefined) return null;
    const soon = d <= 30;
    const label = d <= 0 ? "wordt vandaag opgeschoond" : `wordt over ${d} dag${d === 1 ? "" : "en"} opgeschoond`;
    return <Badge className={`${soon ? "bg-red-100 text-red-700 hover:bg-red-100" : "bg-amber-100 text-amber-700 hover:bg-amber-100"} ml-2`} data-testid="cleanup-badge">{label}</Badge>;
  };
  const ql = q.trim().toLowerCase();
  const filteredTenants = data.tenants.filter((t) => !ql || tName(t).toLowerCase().includes(ql) || (t.client_name || "").toLowerCase().includes(ql) || (t.email1 || "").toLowerCase().includes(ql));
  const filteredClients = data.clients.filter((c) => !ql || (c.name || "").toLowerCase().includes(ql) || (c.client_number || "").toLowerCase().includes(ql));
  const filteredComplaints = complaints.filter((c) => !ql || `${c.number || ""} ${c.title || ""} ${c.tenant_name || ""}`.toLowerCase().includes(ql));

  return (
    <div className="space-y-6" data-testid="archive-page">
      <div>
        <h1 className="font-heading text-2xl font-bold text-[#0F1E3D] flex items-center gap-2"><Archive className="h-6 w-6 text-[#C9A227]" /> Archief</h1>
        <p className="text-sm text-slate-500">Gearchiveerde debiteuren, opdrachtgevers en afgehandelde klachten. Zet ze terug naar de actieve lijst of verwijder ze definitief.</p>
      </div>

      <div className="relative max-w-md">
        <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Zoek op naam, klantnummer of e-mail…" className="pl-9" data-testid="archive-search" />
      </div>

      <Tabs defaultValue="tenants" className="w-full">
        <TabsList>
          <TabsTrigger value="tenants" data-testid="archive-tab-tenants"><Users className="h-4 w-4 mr-1.5" /> Debiteuren ({filteredTenants.length})</TabsTrigger>
          <TabsTrigger value="clients" data-testid="archive-tab-clients"><Briefcase className="h-4 w-4 mr-1.5" /> Opdrachtgevers ({filteredClients.length})</TabsTrigger>
          <TabsTrigger value="complaints" data-testid="archive-tab-complaints"><ShieldAlert className="h-4 w-4 mr-1.5" /> Klachten ({filteredComplaints.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="tenants" className="mt-4">
          <Card className="p-6">
            {filteredTenants.length === 0 ? <p className="text-sm text-slate-400 py-8 text-center">{data.tenants.length === 0 ? "Geen gearchiveerde debiteuren." : "Geen resultaten voor deze zoekopdracht."}</p> : (
              <div className="space-y-2" data-testid="archive-tenants-list">
                {filteredTenants.map((t) => (
                  <div key={t.id} className="flex items-center justify-between border rounded-xl px-4 py-3 hover:bg-slate-50" data-testid={`archive-tenant-${t.id}`}>
                    <div className="min-w-0">
                      <button className="font-medium text-slate-800 hover:text-blue-700 truncate" onClick={() => navigate(`/app/huurders/${t.id}`)}>{tName(t)}</button>
                      <div className="text-xs text-slate-400">{t.client_name || "—"} · gearchiveerd {fmtDate(t.archived_at)}{cleanupBadge(t.cleanup_in_days)}</div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Button size="sm" variant="outline" onClick={() => restoreTenant(t)} data-testid={`archive-tenant-restore-${t.id}`}><ArchiveRestore className="h-4 w-4 mr-1.5" /> Terugzetten</Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button size="icon" variant="ghost" className="h-9 w-9 text-slate-400 hover:text-red-600" title="Definitief verwijderen" data-testid={`archive-tenant-delete-${t.id}`}><Trash2 className="h-4 w-4" /></Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>{tName(t)} definitief verwijderen?</AlertDialogTitle>
                            <AlertDialogDescription>De debiteur én alle bijbehorende contracten, betalingen, brieven, gesprekken, facturen en dossiers worden definitief verwijderd. Dit kan niet ongedaan worden gemaakt.</AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Annuleren</AlertDialogCancel>
                            <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={() => deleteTenant(t)} data-testid={`archive-tenant-delete-confirm-${t.id}`}>Ja, verwijderen</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="clients" className="mt-4">
          <Card className="p-6">
            {filteredClients.length === 0 ? <p className="text-sm text-slate-400 py-8 text-center">{data.clients.length === 0 ? "Geen gearchiveerde opdrachtgevers." : "Geen resultaten voor deze zoekopdracht."}</p> : (
              <div className="space-y-2" data-testid="archive-clients-list">
                {filteredClients.map((c) => (
                  <div key={c.id} className="flex items-center justify-between border rounded-xl px-4 py-3 hover:bg-slate-50" data-testid={`archive-client-${c.id}`}>
                    <div className="min-w-0">
                      <button className="font-medium text-slate-800 hover:text-blue-700 truncate" onClick={() => navigate(`/app/opdrachtgevers/${c.id}`)}>{c.name}</button>
                      <div className="text-xs text-slate-400">{c.property_count} pand(en) · {c.tenant_count} debiteur(en) · gearchiveerd {fmtDate(c.archived_at)}{cleanupBadge(c.cleanup_in_days)}</div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Button size="sm" variant="outline" onClick={() => restoreClient(c)} data-testid={`archive-client-restore-${c.id}`}><ArchiveRestore className="h-4 w-4 mr-1.5" /> Terugzetten</Button>
                      {(c.property_count > 0 || c.tenant_count > 0) ? (
                        <Button size="icon" variant="ghost" className="h-9 w-9 text-slate-300 cursor-not-allowed" title="Kan niet verwijderen: er hangen nog panden/debiteuren aan" disabled data-testid={`archive-client-delete-blocked-${c.id}`}><Trash2 className="h-4 w-4" /></Button>
                      ) : (
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button size="icon" variant="ghost" className="h-9 w-9 text-slate-400 hover:text-red-600" title="Definitief verwijderen" data-testid={`archive-client-delete-${c.id}`}><Trash2 className="h-4 w-4" /></Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>{c.name} definitief verwijderen?</AlertDialogTitle>
                              <AlertDialogDescription>Deze opdrachtgever wordt definitief verwijderd. Dit kan niet ongedaan worden gemaakt.</AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Annuleren</AlertDialogCancel>
                              <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={() => deleteClient(c)} data-testid={`archive-client-delete-confirm-${c.id}`}>Ja, verwijderen</AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="complaints" className="mt-4">
          <Card className="p-6">
            {filteredComplaints.length === 0 ? <p className="text-sm text-slate-400 py-8 text-center">Geen afgehandelde klachten.</p> : (
              <div className="space-y-2" data-testid="archive-complaints-list">
                {filteredComplaints.map((c) => (
                  <div key={c.id} className="flex items-center justify-between gap-3 border rounded-xl px-4 py-3 hover:bg-slate-50" data-testid={`archive-complaint-${c.id}`}>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-slate-800">{c.number && <span className="text-slate-400 font-normal mr-1">{c.number}</span>}{c.title}</span>
                        <Badge variant="outline" className="capitalize">{c.status}</Badge>
                        <DaysOpenBadge c={c} />
                      </div>
                      <div className="text-xs text-slate-400">{c.tenant_name || "—"} · aangemaakt {fmtDate(c.created_at)}{c.closed_at ? ` · gesloten ${fmtDate(c.closed_at)}` : ""}</div>
                    </div>
                    <Button size="sm" variant="outline" className="shrink-0" onClick={() => reopenComplaint(c)} data-testid={`archive-complaint-reopen-${c.id}`}><ArchiveRestore className="h-4 w-4 mr-1.5" /> Heropenen</Button>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
